import React, { useRef } from 'react';
import type { MouseEvent } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { samples } from '../data/samples';
import { useSampleCarousel } from '../hooks/useSampleCarousel';
export function SamplesSection() {
  const {
    current,
    showSample,
    next,
    prev,
    onKeyDown,
    onTouchStart,
    onTouchEnd
  } = useSampleCarousel(samples.length);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const active = samples[current];
  const openDialog = () => dialogRef.current?.showModal();
  const closeOnBackdrop = (event: MouseEvent<HTMLDialogElement>) => {
    const dialog = dialogRef.current;
    if (!dialog || event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
      dialog.close();
    }
  };
  return <section className="section night" id="samples" data-id="element-194" data-mp="3870f6lq0502o">
      <div className="wrap" role="region" aria-roledescription="carousel" aria-label="Volume 1 page samples" data-id="element-195" data-mp="388bmuwmkirz2">
        <div className="section-heading" data-id="element-196" data-mp="1h9b3gcsjrbhc">
          <p className="eyebrow" data-id="element-197" data-mp="1lchkmfh76n2l">Look inside the planning</p>
          <h2 data-id="element-198" data-mp="1lawqgqysp659">
            See how each session <span className="accent" data-id="element-199" data-mp="1qbkbmw0cesrk">does the planning for you.</span>
          </h2>
          <p data-id="element-200" data-mp="1v2spq5cmb2b2">Pages from Volume 1. Open any page in a larger view to read the details.</p>
        </div>
        <div className="sample-grid" onKeyDown={onKeyDown} data-id="element-201" data-mp="2025kdc9qddo7">
          <div className="sample-stage" data-id="element-202" data-mp="201m3lyvtgvbr">
            <div id="sample-slides" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-203" data-mp="250qb8l33ekzz">
              {samples.map((sample, i) => <button key={sample.image} type="button" className="sample-frame" hidden={i !== current} aria-label={`Open larger view: ${sample.label}`} onClick={openDialog} data-id="element-204" data-mp="293va8dnys4jv" data-mpi={i}>
                  <img src={sample.image} alt={sample.alt} data-id="element-205" data-mp="jof4775c6yy7" />
                </button>)}
            </div>
            <div className="carousel-controls" data-id="element-206" data-mp="1myt82jj4luu6">
              <button className="arrow-button prev" type="button" aria-label="Previous page" aria-controls="sample-slides" onClick={prev} data-id="element-207" data-mp="1hzzrgi8jlnps">
                <ArrowRightIcon className="icon" strokeWidth={1.8} aria-hidden="true" data-id="element-208" data-mp="1w0spve1yt2y6" />
              </button>
              <p className="counter" aria-live="polite" data-id="element-209" data-mp="1qytp8bxm0k4x">
                <b data-id="element-210" data-mp="25wlzopoauic9">{String(current + 1).padStart(2, '0')}</b>
                <span data-id="element-211" data-mp="25v2mj7xxwnep"> / {String(samples.length).padStart(2, '0')}</span>
              </p>
              <button className="arrow-button" type="button" aria-label="Next page" aria-controls="sample-slides" onClick={next} data-id="element-212" data-mp="20whu3uoxj80t">
                <ArrowRightIcon className="icon" strokeWidth={1.8} aria-hidden="true" data-id="element-213" data-mp="2anlwzw1bz2fa" />
              </button>
            </div>
          </div>
          <div className="sample-copy" data-id="element-214" data-mp="2ooerigrmtxie">
            <p className="eyebrow" data-id="element-215" data-mp="2jotwuijhwbbh">{active.label}</p>
            <h3 data-id="element-216" data-mp="2jqdbk4pi6eb5">{active.title}</h3>
            <p data-id="element-217" data-mp="2jmz80t53rz24">{active.summary}</p>
            <div className="sample-thumbs" aria-label="Choose a page" data-id="element-218" data-mp="33ac5k0m03tc8">
              {samples.map((sample, i) => <button key={sample.image} type="button" aria-label={`Show ${sample.label}`} aria-current={i === current} onClick={() => showSample(i)} data-id="element-219" data-mp="388dpsov5xypa" data-mpi={i}>
                  <img src={sample.image} alt="" data-id="element-220" data-mp="2kkdy9mq1249n" />
                  <span data-id="element-221" data-mp="2kijfof28ao8q">{sample.thumb}</span>
                </button>)}
            </div>
            <button type="button" className="sample-expand" onClick={openDialog} data-id="element-222" data-mp="2pcdwnaqg8p44">
              Open a larger view ↗
            </button>
          </div>
        </div>
      </div>
      <dialog className="sample-dialog" ref={dialogRef} aria-labelledby="dialog-title" onClick={closeOnBackdrop} data-id="element-223" data-mp="2fm2otd3n9vry">
        <button type="button" className="dialog-close" aria-label="Close larger view" onClick={() => dialogRef.current?.close()} data-id="element-224" data-mp="21lrz4de2mn8d">
          ×
        </button>
        <h3 id="dialog-title" data-id="element-225" data-mp="1yk6nqtcrlr2a">{active.label}</h3>
        <img src={active.image} alt={active.alt} data-id="element-226" data-mp="1yivjksfeqhan" />
      </dialog>
    </section>;
}